import { useEffect, useMemo, useState, useCallback } from 'react';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  fetchJson,
  type RecordItem,
  type Questionnaire,
  type Question,
  type Cycle
} from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

function defaultMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export default function Records() {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [activeQnId, setActiveQnId] = useState<string>('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [items, setItems] = useState<RecordItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const initial = defaultMonthRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [cycleFilter, setCycleFilter] = useState<string>('');
  const [showAllStatus, setShowAllStatus] = useState(false);

  // initial questionnaires load
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchJson<Questionnaire[]>('/api/questionnaires'),
      fetchJson<{ items: Cycle[] }>('/api/cycles?pageSize=200')
    ]).then(([qns, cy]) => {
      if (cancelled) return;
      setQuestionnaires(qns);
      setCycles(cy.items);
      const firstActive = qns.find((q) => q.isActive) || qns[0];
      if (firstActive) setActiveQnId(firstActive.id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // when questionnaire changes - load its questions
  useEffect(() => {
    if (!activeQnId) return;
    fetchJson<Question[]>(`/api/questionnaires/${activeQnId}/questions`).then(setQuestions);
  }, [activeQnId]);

  const refresh = useCallback(async () => {
    if (!activeQnId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        questionnaireId: activeQnId,
        pageSize: '200'
      });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (cycleFilter) params.set('cycleId', cycleFilter);
      if (showAllStatus) params.set('status', 'all');
      const res = await fetchJson<{ items: RecordItem[]; total: number }>(
        `/api/records?${params}`
      );
      setItems(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [activeQnId, from, to, cycleFilter, showAllStatus]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const exportUrl = () => {
    const params = new URLSearchParams({ questionnaireId: activeQnId });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (cycleFilter) params.set('cycleId', cycleFilter);
    if (showAllStatus) params.set('status', 'all');
    return `/api/records/export.xlsx?${params}`;
  };

  const answerColumns = useMemo(
    () =>
      questions
        .filter((q) => q.inputType !== 'announcement')
        .sort((a, b) => a.orderIndex - b.orderIndex),
    [questions]
  );

  const columns: DataTableColumn<RecordItem>[] = useMemo(() => {
    const base: DataTableColumn<RecordItem>[] = [
      { key: 'customerName', header: 'שם לקוח', cell: (s) => s.customerName || '-' },
      {
        key: 'phone',
        header: 'טלפון',
        cell: (s) => (
          <span className="font-mono text-xs" dir="ltr">
            {s.customerPhone || s.callerPhone || ''}
          </span>
        )
      },
      {
        key: 'startedAt',
        header: 'תאריך',
        cell: (s) => <span className="text-sm">{formatDateTime(s.startedAt)}</span>
      },
      {
        key: 'cycle',
        header: 'מחזור',
        cell: (s) => (
          <span className="text-xs">
            {s.cycleName || (s.cycleStartsAt ? formatDateTime(s.cycleStartsAt) : '-')}
          </span>
        )
      },
      {
        key: 'status',
        header: 'סטטוס',
        cell: (s) => (
          <Badge variant={s.status === 'completed' ? 'success' : 'muted'}>
            {s.status === 'completed' ? 'הושלם' : s.status === 'abandoned' ? 'נטוש' : s.status}
          </Badge>
        )
      }
    ];
    for (const q of answerColumns) {
      base.push({
        key: `q_${q.id}`,
        header: q.label,
        cell: (s) => {
          const a = s.answers[q.key];
          return a?.value_num ?? a?.value_text ?? '';
        }
      });
    }
    return base;
  }, [answerColumns]);

  return (
    <div className="page-fill flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">רשומות</h1>
          <p className="text-sm text-muted-foreground">{total} רשומות בסינון הנוכחי</p>
        </div>
        <Button asChild variant="outline" size="sm" disabled={!activeQnId}>
          <a href={activeQnId ? exportUrl() : '#'} download>
            <Download className="h-4 w-4" />
            ייצא לאקסל
          </a>
        </Button>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader className="flex-none">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="space-y-1">
              <Label>שאלון</Label>
              <Select value={activeQnId} onValueChange={setActiveQnId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {questionnaires.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>מתאריך</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>עד תאריך</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>מחזור</Label>
              <Select value={cycleFilter || 'all'} onValueChange={(v) => setCycleFilter(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="כל המחזורים" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">כל המחזורים</SelectItem>
                  {cycles.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name || formatDateTime(c.startsAt)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Switch
              checked={showAllStatus}
              onCheckedChange={setShowAllStatus}
              id="show_all_status"
            />
            <Label htmlFor="show_all_status" className="text-sm font-normal text-muted-foreground">
              כלול גם רשומות לא הושלמו (in_progress / abandoned)
            </Label>
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <DataTable
            columns={columns}
            data={items}
            getRowKey={(s) => s.id}
            loading={loading}
            emptyMessage="אין רשומות."
            pagination
          />
        </CardContent>
      </Card>
    </div>
  );
}
